import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { Calendar, CheckCircle, CreditCard, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { getToken, getUser } from "@/utils/auth";
import { useSettings } from "@/context/SettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { formatCurrency, formatDate } = useSettings();
  const user = getUser();
  const [memberData, setMemberData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberReceipts, setMemberReceipts] = useState([]);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await api.get('/members/me');
      // Handle different response structures
      const member = response.data.member || (response.data.id ? response.data : null);
      const resUser = response.data.user || (response.data.member_id ? null : response.data);
      
      if (!member) {
        try {
          const membersResp = await api.get('/members');
          const u = resUser || user;
          const candidate = membersResp.data.find((m) =>
            (u?.member_id && m.id === u.member_id) ||
            (u?.email && (m.email || '').toLowerCase() === (u.email || '').toLowerCase())
          );
          if (candidate) {
            const detailResp = await api.get(`/members/${candidate.id}`);
            const m = detailResp.data;
            setMemberData(m);
            setSubscriptions(m.subscriptions || []);
            setAttendance(m.attendance || []);
            setPayments(m.payments || []);
            await loadMemberLedgerReceipts(m.name);
          } else {
            setMemberData(resUser || user);
            setSubscriptions([]);
            setAttendance([]);
            setPayments([]);
            await loadMemberLedgerReceipts(resUser?.full_name || user?.full_name || '');
          }
        } catch (e) {
          setMemberData(resUser || user);
          setSubscriptions([]);
          setAttendance([]);
          setPayments([]);
          await loadMemberLedgerReceipts(resUser?.full_name || user?.full_name || '');
        }
      } else {
        setMemberData(member);
        setSubscriptions(response.data.subscriptions || []);
        setAttendance(response.data.attendance || []);
        setPayments(response.data.payments || []);
        await loadMemberLedgerReceipts(member.name);
      }
      setLoading(false);
    } catch (error) {
      console.error("Failed to load member data:", error);
      const detail = error.response?.data?.detail || "";
      if (error.response?.status === 404 && detail.toLowerCase().includes("member not found")) {
        try {
          const membersResp = await api.get('/members');
          const u = user;
          const candidate = membersResp.data.find((m) =>
            (u?.member_id && m.id === u.member_id) ||
            (u?.email && (m.email || '').toLowerCase() === (u.email || '').toLowerCase())
          );
          if (candidate) {
            const detailResp = await api.get(`/members/${candidate.id}`);
            const m = detailResp.data;
            setMemberData(m);
            setSubscriptions(m.subscriptions || []);
            setAttendance(m.attendance || []);
            setPayments(m.payments || []);
            await loadMemberLedgerReceipts(m.name);
          } else {
            setMemberData(user);
            setSubscriptions([]);
            setAttendance([]);
            setPayments([]);
            await loadMemberLedgerReceipts(user?.full_name || '');
          }
        } catch (e) {
          setMemberData(user);
          setSubscriptions([]);
          setAttendance([]);
          setPayments([]);
          await loadMemberLedgerReceipts(user?.full_name || '');
        }
        setLoading(false);
      } else {
        toast.error(detail || "Failed to load data");
        setLoading(false);
      }
    }
  };

  const loadMemberLedgerReceipts = async (memberName) => {
    try {
      const ledgerResp = await api.get('/ledger');
      const entries = Array.isArray(ledgerResp.data) ? ledgerResp.data : [];
      const receipts = entries.filter((e) =>
        e.type === 'receipt' && (e.member_name || '').toLowerCase() === (memberName || '').toLowerCase()
      ).sort((a, b) => new Date(b.date) - new Date(a.date));
      setMemberReceipts(receipts);
    } catch (err) {
      setMemberReceipts([]);
    }
  };

  const calculatePendingPayments = () => {
    const pendingItems = [];
    if (!memberData) return pendingItems;

    // Pending Admission Fee
    if (!memberData.admission_fee_paid && memberData.admission_fee_amount > 0) {
      pendingItems.push({
        id: 'admission',
        type: 'Admission Fee',
        description: 'One-time admission fee',
        amount: memberData.admission_fee_amount,
        date: memberData.join_date,
        isAdmission: true
      });
    }

    // Pending Subscriptions
    subscriptions.forEach(sub => {
      const pendingAmount = (sub.amount_due || 0) - (sub.amount_paid || 0);
      if ((sub.payment_status === 'pending' || sub.payment_status === 'partial') && pendingAmount > 0) {
        pendingItems.push({
          id: sub.id,
          type: 'Subscription',
          description: sub.plan?.name || 'Unknown Plan',
          amount: pendingAmount,
          date: sub.start_date,
          isSubscription: true
        });
      }
    });

    return pendingItems;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  const pendingPayments = calculatePendingPayments();

  // Get display subscriptions (Active ones, or most recent if no active)
  const sortedSubscriptions = [...subscriptions].sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
  const activeSubscriptions = sortedSubscriptions.filter(sub => sub.status === 'active');
  const displaySubscriptions = activeSubscriptions.length > 0 ? activeSubscriptions.slice(0, 2) : sortedSubscriptions.slice(0, 2);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Welcome, {user?.full_name}!
        </h1>
        <p className="text-zinc-500">View your membership and activity</p>
      </div>

      {/* Pending Payments Alert */}
      {pendingPayments.length > 0 && (
        <div className="mb-8 bg-orange-50 border border-orange-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-orange-200 bg-orange-100/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-orange-600" size={20} />
              <h2 className="text-xl font-bold text-orange-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Pending Payments
              </h2>
            </div>
            <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
              {pendingPayments.length} Pending
            </Badge>
          </div>
          <div className="divide-y divide-orange-200/50">
            {pendingPayments.map((payment) => (
              <div key={payment.id} className="px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between hover:bg-orange-100/30 transition-colors gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-zinc-900">{payment.type}</span>
                    <span className="text-zinc-500">•</span>
                    <span className="text-zinc-700">{payment.description}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-zinc-500">
                    <Calendar size={14} />
                    <span>Due since: {formatDate(payment.date)}</span>
                  </div>
                </div>
                <div className="text-lg font-bold text-orange-700 w-full md:w-auto text-right md:text-left">
                  {formatCurrency(payment.amount)}
                </div>
              </div>
            ))}
            <div className="px-6 py-3 bg-orange-100/30 flex justify-end items-center gap-2 border-t border-orange-200/50">
              <span className="text-sm font-medium text-orange-800">Total Pending:</span>
              <span className="text-xl font-black text-orange-700">
                {formatCurrency(pendingPayments.reduce((sum, p) => sum + p.amount, 0))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Member Info Card */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm mb-8">
        <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Your Profile
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-zinc-500">Email</p>
            <p className="font-medium text-zinc-900">{user?.email}</p>
          </div>
          <div>
            <p className="text-sm text-zinc-500">Phone</p>
            <p className="font-medium text-zinc-900">{user?.phone}</p>
          </div>
          <div>
            <p className="text-sm text-zinc-500">Company</p>
            <p className="font-medium text-zinc-900">{user?.company?.name || user?.tenant?.name || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div 
          onClick={() => navigate('/member/subscriptions')}
          className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Subscriptions</p>
            <CreditCard className="text-purple-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{subscriptions.length}</p>
          <p className="text-xs text-zinc-500 mt-1">total</p>
        </div>

        <div 
          onClick={() => navigate('/member/attendance')}
          className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Attendance</p>
            <CheckCircle className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{attendance.length}</p>
          <p className="text-xs text-zinc-500 mt-1">total visits</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">This Month</p>
            <Calendar className="text-lime-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">0</p>
          <p className="text-xs text-zinc-500 mt-1">visits</p>
        </div>

        <div 
          onClick={() => navigate('/member/payments')}
          className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Total Paid</p>
            <CreditCard className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">
            {formatCurrency(memberReceipts.reduce((sum, r) => sum + (r.amount || 0), 0))}
          </p>
        </div>
      </div>


      {/* Subscriptions */}
      {displaySubscriptions.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>Your Current Subscription</h3>
            {subscriptions.length > displaySubscriptions.length && (
              <button 
                onClick={() => navigate('/member/subscriptions')}
                className="text-sm text-lime-600 hover:text-lime-700 font-medium"
              >
                View History
              </button>
            )}
          </div>
          <div className="space-y-3">
            {displaySubscriptions.map((sub) => (
              <div key={sub.id} className="flex flex-col md:flex-row justify-between items-start md:items-center p-3 bg-zinc-50 rounded-lg gap-2">
                <div className="w-full md:w-auto">
                  <div className="flex items-center justify-between md:justify-start gap-2">
                    <p className="font-medium">{sub.plan?.name}</p>
                    <Badge variant="outline" className={
                      sub.status === 'active' ? 'bg-lime-100 text-lime-800 border-lime-200' : 'bg-zinc-100 text-zinc-800 border-zinc-200'
                    }>
                      {sub.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-zinc-500 mt-1">
                    {formatDate(sub.start_date)} - {formatDate(sub.end_date)}
                  </p>
                </div>
                <div className="text-left md:text-right w-full md:w-auto mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-zinc-200">
                  {sub.payment_status === 'paid' ? (
                    <div className="flex justify-between md:block">
                      <p className="font-bold">{formatCurrency((sub.amount_due || sub.plan?.price || 0))}</p>
                      <p className="text-sm text-green-600">Paid</p>
                    </div>
                  ) : (
                    <div className="w-full">
                      <div className="flex justify-between md:block">
                        <p className="font-medium text-sm md:text-base">Total: {formatCurrency((sub.amount_due || sub.plan?.price || 0))}</p>
                        <p className="text-sm text-green-700">Paid: {formatCurrency((sub.amount_paid || 0))}</p>
                      </div>
                      <p className="text-sm text-orange-700 md:text-right">Balance: {formatCurrency(((sub.amount_due || sub.plan?.price || 0) - (sub.amount_paid || 0)))}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payments */}
      {memberReceipts.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>Recent Payments</h3>
            {memberReceipts.length > 5 && (
              <button 
                onClick={() => navigate('/member/payments')}
                className="text-sm text-lime-600 hover:text-lime-700 font-medium"
              >
                View All
              </button>
            )}
          </div>
          <div className="space-y-3">
            {memberReceipts.slice(0, 5).map((r) => (
              <div key={r.id} className="flex flex-col md:flex-row justify-between items-start md:items-center p-3 bg-zinc-50 rounded-lg gap-2">
                <div className="w-full md:w-auto">
                  <div className="flex justify-between items-start">
                    <p className="font-medium">{r.category}</p>
                    <p className="font-bold md:hidden">{formatCurrency(r.amount || 0)}</p>
                  </div>
                  <p className="text-sm text-zinc-500">
                    {formatDate(r.date)} • {(r.payment_method || '').replace('_', ' ')}
                  </p>
                  {r.description && (
                    <p className="text-xs text-zinc-500 mt-1">{r.description}</p>
                  )}
                </div>
                <div className="hidden md:block text-right">
                  <p className="font-bold">{formatCurrency(r.amount || 0)}</p>
                  {r.reference_number && (
                    <p className="text-xs text-zinc-400 mt-1">Ref: {r.reference_number}</p>
                  )}
                </div>
                {/* Mobile only reference number if exists */}
                {r.reference_number && (
                  <div className="md:hidden w-full pt-2 mt-1 border-t border-zinc-200">
                    <p className="text-xs text-zinc-400">Ref: {r.reference_number}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-bold text-blue-900 mb-2">Member Portal</h3>
        <p className="text-sm text-blue-800">
          This is your member dashboard. Your attendance, subscriptions, and payment history will appear here.
          Contact your administrator for more information.
        </p>
      </div>
    </div>
  );
};

export default MemberDashboard;
